import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useLazyGetPreferredLanguageQuery,
  useUpdatePreferredLanguageMutation,
} from "../store/api/userPreferencesApiSlice";
import {
  applyPreferredLanguageToI18n,
  extractPreferredLanguageCode,
  getPreferredLanguageCodeFromI18n,
  normalizeI18nLanguage,
} from "../utils/preferredLanguage";

const usePreferredLanguage = () => {
  const { i18n, t } = useTranslation();
  const [getPreferredLanguage] = useLazyGetPreferredLanguageQuery();
  const [updatePreferredLanguage, { isLoading: isUpdating }] =
    useUpdatePreferredLanguageMutation();
  const [preferredLanguageCode, setPreferredLanguageCode] = useState(() =>
    getPreferredLanguageCodeFromI18n(i18n.language)
  );
  const lastLocalChangeAtRef = useRef(0);

  const currentLanguage = normalizeI18nLanguage(
    i18n.resolvedLanguage || i18n.language
  );
  const isTurkish = currentLanguage === "tr";

  const syncPreferredLanguage = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const res = await getPreferredLanguage(undefined, false).unwrap();
      if (startedAt < lastLocalChangeAtRef.current) return null;

      const code = extractPreferredLanguageCode(res);
      if (code != null) {
        setPreferredLanguageCode(code);
        await applyPreferredLanguageToI18n(code);
      }
      return code;
    } catch (error) {
      console.warn("Preferred language sync failed", error);
      return null;
    }
  }, [getPreferredLanguage]);

  const changePreferredLanguage = useCallback(
    async (lng) => {
      const code = getPreferredLanguageCodeFromI18n(lng);
      if (code == null) return false;

      const previousCode = getPreferredLanguageCodeFromI18n(
        i18n.resolvedLanguage || i18n.language
      );

      lastLocalChangeAtRef.current = Date.now();
      setPreferredLanguageCode(code);
      await applyPreferredLanguageToI18n(code);

      try {
        const res = await updatePreferredLanguage({
          preferredLanguageCode: code,
        }).unwrap();
        const savedCode = extractPreferredLanguageCode(res) ?? code;
        setPreferredLanguageCode(savedCode);
        await applyPreferredLanguageToI18n(savedCode);
        return true;
      } catch (error) {
        lastLocalChangeAtRef.current = Date.now();
        setPreferredLanguageCode(previousCode);
        await applyPreferredLanguageToI18n(previousCode);
        const errCode = error?.data?.error?.key ?? error?.data?.error?.code;
        if (errCode === "USR-00001") {
          toast.error(t("invalid_language_code"));
        } else {
          toast.error(t("error_occured"));
        }
        return false;
      }
    },
    [updatePreferredLanguage, t, i18n.language, i18n.resolvedLanguage]
  );

  return {
    preferredLanguageCode,
    currentLanguage,
    isTurkish,
    isUpdating,
    syncPreferredLanguage,
    changePreferredLanguage,
  };
};

export default usePreferredLanguage;
